import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ContactsListView from "./ContactsList/ContactsListView";
import "../ContactsView/Contacts/Contacts.css";
import ContactDetailsView from "./ContactsDetails/ContactDetailsView";
import { removeContacts, acceptContacts, ignoreContacts } from "../../services/clients/ContactsService";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import {
	removeContactStatus,
	clearAcceptIgnoreContact,
	syncContactsWithCache,
	selectedContactAction,
	updatePendingContacts,
	removeSelectedContact,
	fetchContacts,
} from "../../state/actions/contacts";
import { clearAcceptIgnoreButtons, createConversation, getFavourite, setSelectedContactData, setSelectedOnetoOneChat } from "../../state/actions/chats";
import store from "../../state/configureStore";
import {
	getDataFromLFStorage,
	LFStorageKeys,
	saveDataInLFStorage,
} from "../../services/storage/LFStorage";
import { setCallHistory } from "../../state/actions/user";
import { useNavigate } from "react-router-dom";
import ModalNewUI from "../../components/ui/Modals/ModalMessages/ModalNewUI";
import { removeBot, setDomainState } from "../../state/actions/domain";
import { getAuthData } from "../../services/storage/LocalStorage";
import { setHideDefaultHomepageBot } from "../../state/actions/bot";
import { setHeaderTitle } from "../../state/actions/layout";
import { ImagePath } from "../../assets/ImagePath";
import GroupCreateModal from "../../components/ui/Modals/GroupCreateModal";

import DeleteAccountModal from "../../components/ui/Modals/DeleteAccountModal";

const ContactsView = (props) => {

	const dispatch = useDispatch();
	const navigate = useNavigate();
	// Redux selectors
	const userDetail = getAuthData();
	const userData = userDetail.user || {};
	const [addContactMenu, setAddContactMenu] = useState(false);
	const user = useSelector((state) => state.user);
	const userId = userDetail.user?.userId;
	const allContacts = useSelector((state) => state.contacts.allContacts);
	const contentList = useSelector((state) => state.contacts.contentList);
	const pendingContacts = useSelector((state) => state.contacts.pendingContacts);
	const selectContact = useSelector((state) => state.contacts.selectedContact);
	const contactsCallHistory = useSelector((state) => state.user.contactsCallHistory);
	const contactRemovingStatus = useSelector((state) => state.contacts.contactRemovingStatus);
	const allProfileImages = useSelector((state) => state.profileImages || {});
	const selectedDomain = useSelector((state) => state.domain.selectedDomainDetails);
	const [selectAction, setSelectAction] = useState(false)
	const [openGroupModal, setOpenGroupModal] = useState(false)
	const isSideBar = useSelector((state) => state.layout.isSideBar);
	const [contactToRemove, setContactToRemove] = useState(null);
	const [selectedContactForChat, setSelectedContactForChat] = useState(null);
	const [isMobile, setIsMobile] = useState(false)
	const [groupContacts, setGroupContacts] = useState([]);
	const pendingTimersRef = useRef([]);

	const trackTimer = useCallback((fn, delay) => {
		const id = setTimeout(fn, delay);
		pendingTimersRef.current.push(id);
		return id;
	}, []);

	// Cleanup all tracked timers on unmount
	useEffect(() => {
		return () => {
			pendingTimersRef.current.forEach(id => clearTimeout(id));
			pendingTimersRef.current = [];
		};
	}, []);

	useEffect(() => {
		if(selectedDomain?.userDomain){
         dispatch(fetchContacts({ selectedDomain: selectedDomain?.userDomain }))
		}
	}, []);

	const contactLists = useMemo(() => ({
		accepted: allContacts?.accepted || [],
		localContacts: allContacts?.localContacts || [],
		vessels: allContacts?.vessels || []
	}), [allContacts]);

	const createContactFilter = useCallback((excludeArray) => {
		return function (current) {
			return !excludeArray.some(other => other.userId === current.userId);
		};
	}, []);

	useEffect(() => {
		const checkScreenSize = () => setIsMobile(window.innerWidth < 1024);
		checkScreenSize();

		window.addEventListener("resize", checkScreenSize);
		return () => window.removeEventListener("resize", checkScreenSize);
	}, []);

	const selectContactHandler = useCallback((contact, imgSrc) => {
		if (!contact) return;

		const contactWithImage = { ...contact, profilePhoto: imgSrc };
		dispatch(selectedContactAction(contactWithImage));

		const selectedContactsCallHistory = contactsCallHistory[contact.userId];
		if (selectedContactsCallHistory?.length === 0) {
			dispatch(setCallHistory(contact.userId));
		}
	}, [dispatch, contactsCallHistory]);

	const startConversation = useCallback((contact) => {
		const userId = user?.user?.userId ?? userData.userId
		dispatch(removeBot())
		dispatch(setHideDefaultHomepageBot());
		dispatch(setSelectedOnetoOneChat(true));
		dispatch(setSelectedContactData(contact));
		dispatch(createConversation(userId, null, selectedDomain?.userDomain , contact));
		dispatch(setHeaderTitle("Connect"));
		navigate("/app/messages");
	}, [props]);

	const startRemoveContact = useCallback((contact) => {
		setContactToRemove(contact);
	}, []);

	const cancelRemoveContact = useCallback(() => {
		setContactToRemove(null);
	}, []);

	const startChatWithContact = useCallback((contact) => {
		setSelectedContactForChat(contact);
	}, []);

	const removeContact = useCallback(async () => {
		if (!contactToRemove) return;

		store.dispatch(removeContactStatus(false));
		const type = contactToRemove.contactType || "frontm";
		const dataToDelete = { ...contactToRemove };

		try {
			if (type === "local") {
				await removeContacts(dataToDelete, type, selectedDomain?.userDomain );
			} else {
				await removeContacts([contactToRemove.userId], type, selectedDomain?.userDomain );
			}

			removeSelectedContact();
			setContactToRemove(null);

			trackTimer(() => {
				if (type === "local") {
					getFavourite({
						selectedDomain: selectedDomain?.userDomain ,
					});
				} else {
					dispatch(getFavourite({
						selectedDomain: selectedDomain?.userDomain ,
					}));
				}
				store.dispatch(removeContactStatus(true));
			}, 3000);

		} catch (error) {
			console.error('Error removing contact:', error);
		}
	}, [contactToRemove, selectedDomain?.userDomain , props, dispatch]);

	const cacheContactAction = useCallback(async (user, action) => {
		try {
			const contactsList = await getDataFromLFStorage(
				`${LFStorageKeys.CONTACTS}_${selectedDomain?.userDomain }`
			) || {};

			if (!contactsList.contacts) return;

			if (action === "accept") {
				contactsList.contacts.forEach((contact) => {
					if (contact.userId === user.userId) {
						contact.showAcceptIgnoreMsg = false;
						dispatch(selectedContactAction(contact));
					}
				});
			} else if (action === "ignore") {
				contactsList.contacts = contactsList.contacts.filter(
					(contact) => contact.userId !== user.userId
				);

				if (!contactsList.ignored) contactsList.ignored = [];
				contactsList.ignored.push({
					userId: user.userId,
					userName: user.userName,
				});
				props.removeSelectedContact();
			}

			await saveDataInLFStorage(
				`${LFStorageKeys.CONTACTS}_${selectedDomain?.userDomain }`,
				contactsList
			);
			store.dispatch(syncContactsWithCache(contactsList));
		} catch (error) {
			console.error('Error caching contact action:', error);
		}
	}, [selectedDomain?.userDomain , dispatch, props]);

	const accept = useCallback(async (contactUserId) => {
		if (!selectContact) return;

		cacheContactAction(selectContact, "accept");
		const updateSelectedContact = selectContact.userId === contactUserId;
		const updatedPendingRequests = pendingContacts?.filter(
			(item) => item.userId !== contactUserId
		);
		try {
			await acceptContacts([contactUserId], selectedDomain?.userDomain );

			trackTimer(() => {
				if (updateSelectedContact) {
					store.dispatch(selectedContactAction({}));
				}
				store.dispatch(updatePendingContacts(updatedPendingRequests));
				clearAcceptIgnoreButtons();
				clearAcceptIgnoreContact();
			}, 3000);
		} catch (error) {
			console.error('Error accepting contact:', error);
		}
	}, [selectContact, pendingContacts, selectedDomain?.userDomain , cacheContactAction, props]);

	const ignore = useCallback(async (contactUserId) => {
		if (!selectContact) return;

		cacheContactAction(selectContact, "ignore");
		store.dispatch(removeContactStatus(false));
		const updateSelectedContact = selectContact.userId === contactUserId;
		const updatedPendingRequests = pendingContacts?.filter(
			(item) => item.userId !== contactUserId
		);

		try {
			await ignoreContacts([contactUserId], selectedDomain?.userDomain );

			trackTimer(() => {
				store.dispatch(removeContactStatus(true));
				if (updateSelectedContact) {
					store.dispatch(selectedContactAction({}));
				}
				store.dispatch(updatePendingContacts(updatedPendingRequests));
				store.dispatch(removeSelectedContact())
				clearAcceptIgnoreButtons();
				clearAcceptIgnoreContact();
				navigate("/app/contacts", { replace: true });
			}, 3000);
		} catch (error) {
			console.error('Error ignoring contact:', error);
		}
	}, [selectContact, pendingContacts, selectedDomain?.userDomain , cacheContactAction, props]);

	useEffect(() => {
		if (selectContact?.userId) {
			dispatch(setCallHistory(selectContact.userId));
		}
	}, [selectContact?.userId, dispatch]);

	useEffect(() => {
		const locationContact = props.location?.state?.selectedContact;
		if (locationContact && props.location?.state?.onlyVoip) {
			dispatch(selectedContactAction(locationContact));
		}
	}, [navigate, dispatch]);

	const gridCols = (!isSideBar && !isMobile)
		? 'xl:grid-cols-[40%_60%] lg:grid-cols-[40%_60%]'
		: 'xl:grid-cols-[30%_70%] lg:grid-cols-[35%_65%]';

	const closeGroupModal = () => {
		setOpenGroupModal(false)
	}

	return (
		<div
			className={`Contact-contentarea grid ${gridCols}`}
			style={{
				background: "#F3F3FA",
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
		>
			<ContactsListView
				{...props}
				startChatWithContact={startChatWithContact}
				accepted={contactLists.accepted}
				setAddContactMenu={setAddContactMenu}
				addContactMenu={addContactMenu}
				user={user}
				localContacts={contactLists.localContacts}
				vessels={contactLists.vessels}
				allProfileImages={allProfileImages}
				style={{ flex: 1 }}
				selectContact={selectContactHandler}
				selectedId={selectContact?.userId || null}
				userId={userId}
			/>
			<ContactDetailsView
				{...props}
				user={user}
				userId={userId}
				allProfileImages={allProfileImages}
				setAddContactMenu={setAddContactMenu}
				addContactMenu={addContactMenu}
				selectedDomain={selectedDomain}
				contact={selectContact}
				startConversation={startConversation}
				remove={startRemoveContact}
				accept={accept}
				ignore={ignore}
				sendMsgForBanner={props.sendMsgForBanner}
			/>
			{contactToRemove && (
				<ModalNewUI
					onClose={cancelRemoveContact}
					open={!!contactToRemove}
					className="!text-size-14"
					headerContent={`Are you sure you want to remove ${contactToRemove?.userName}?`}
				>
					<DeleteAccountModal
						onCloseFn={cancelRemoveContact}
						onConfirmFn={removeContact}
					/>
				</ModalNewUI>
			)}
			<div className="absolute [bottom:157px] [right:30px] w-[60px] h-[60px" onClick={() => { setSelectAction(!selectAction) }}>
				<img src={ImagePath.addPlusIcon} alt="addPlusIcon" />
			</div>
			{selectAction &&
				<div className="w-[360px] px-[20px] py-[25px] bg-[#fff] flex flex-col gap-[12px]  rounded-[10px] shadow-md shadow-[rgba(37,73,242,0.15)] absolute [bottom:228px] [right:42px] ">
					<button className="w-full flex items-center space-x-2 bg-[#F9F9FD] text-size-14 font-normal p-[12px]  rounded-[8px]" onClick={() => setAddContactMenu(true)}>
						<img src={ImagePath.editGroupLogo} />
						<span>Add Contact</span>
					</button>
					<button className="w-full flex items-center space-x-2 bg-[#F9F9FD] text-[16px] font-normal p-[12px]  rounded-[8px] m-0" onClick={() => { setOpenGroupModal(true) }}>
						<img src={ImagePath.exitGroupLogo} />
						<span>Create a Group</span>
					</button>
				</div>
			}
			{openGroupModal &&
				<ModalNewUI
					onClose={closeGroupModal}
					headerContent={''}
					open={openGroupModal}
					customDialogStyles={{ width: '1050px', maxWidth: '1200px', height: 'auto', overflowY: 'hidden' }}
				>
					<GroupCreateModal
						groupContacts={contentList}
						userId={userId}
						closeGroupModal={closeGroupModal}
					/>
				</ModalNewUI>}
		</div>
	);
};

export default ContactsView;